import React, { useCallback, useEffect, useState } from 'react';
import apiClient from '@/api/axios';

export interface DocketParalegalStatus {
  enabled: boolean;
  delegation_id: string | null;
  status: string | null;
  timezone: string | null;
  local_time: string | null;
  weekdays: number[] | null;
  /** 4c: the drafting companion, on and off with the brief. */
  drafter?: { enabled: boolean; delegation_id: string | null };
}

const path = '/agents/delegations/docket-paralegal';

/** Docket Paralegal 5.3 (2026-10-23): a standing instruction in the lawyer's words, executed by the docket and court-mail hooks. */
export interface DocketWatchRule {
  rule_id: string;
  name: string;
  description: string;
  status: 'active' | 'paused' | 'deleted';
  trigger_kind: 'docket_event' | 'court_mail';
  scope_kind: 'lawyer' | 'matter';
  matter_id: string | null;
  fired_count: number;
  last_fired_at: string | null;
}

export const watchRulesApi = {
  async list(): Promise<DocketWatchRule[]> {
    const response = await apiClient.get<{ watch_rules: DocketWatchRule[] }>(`${path}/watch-rules`);
    return Array.isArray(response.data.watch_rules) ? response.data.watch_rules : [];
  },
  async setStatus(ruleId: string, status: 'active' | 'paused' | 'deleted'): Promise<void> {
    await apiClient.post(`${path}/watch-rules/${ruleId}`, { status });
  },
};

export const docketParalegalApi = {
  async status(): Promise<DocketParalegalStatus> {
    const response = await apiClient.get<{ docket_paralegal: DocketParalegalStatus }>(path);
    return response.data.docket_paralegal;
  },
  /** First sign-in: issue the lawyer's paralegal if they never had one. Idempotent; never re-enables one turned off. */
  async ensure(): Promise<DocketParalegalStatus> {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const response = await apiClient.post<{ docket_paralegal: DocketParalegalStatus }>(`${path}/ensure`, { timezone });
    return response.data.docket_paralegal;
  },
  async set(enabled: boolean): Promise<DocketParalegalStatus> {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const response = await apiClient.post<{ docket_paralegal: DocketParalegalStatus }>(path, { enabled, timezone });
    return response.data.docket_paralegal;
  },
};

function describeTime(localTime: string | null): string {
  const [h, m] = (localTime || '07:30').split(':').map(Number);
  const date = new Date();
  date.setHours(h, m, 0, 0);
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/**
 * Docket Paralegal (plan Phase 4): one switch for the lawyer's weekday-morning brief across all their Matters,
 * delivered to them in EdgeMessage. It reads only what they can see and changes nothing.
 */
export const DocketParalegalCard: React.FC = () => {
  const [state, setState] = useState<DocketParalegalStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setState(await docketParalegalApi.status());
    } catch {
      setState(null);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const toggle = async (enabled: boolean) => {
    try {
      setBusy(true);
      setError(null);
      setState(await docketParalegalApi.set(enabled));
    } catch {
      setError(enabled ? 'Could not turn on your Docket Paralegal.' : 'Could not turn off your Docket Paralegal.');
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;
  const time = describeTime(state.local_time);

  return (
    <div className="space-y-2">
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Docket Paralegal: {state.enabled ? 'On' : 'Off'}
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
          {state.enabled
            ? `Each weekday at ${time}${state.timezone ? ` (${state.timezone})` : ''} you get a brief in EdgeMessage across all your Matters: overdue, due this week, what came in from the courts, what awaits your review, and stale dockets. Court event facts it reads from docket entries and court mail wait for your approval, and when an obligation calls for a filing or a reply it proposes a task with a drafted starting point.`
            : `Get a brief each weekday at ${time} across all your Matters: overdue, due this week, what came in from the courts, what awaits your review, and stale dockets; proposed court event facts and drafted starting points for filings and replies, each waiting for your approval. It reads only what you can see and changes nothing on its own.`}
        </p>
        {error && <p className="text-xs text-red-600 dark:text-red-400 mt-1">{error}</p>}
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={() => void toggle(!state.enabled)}
        className={state.enabled
          ? 'px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50'
          : 'px-3 py-1.5 text-sm rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50'}
      >
        {busy ? 'Saving…' : state.enabled ? 'Turn off' : 'Turn on'}
      </button>
    </div>
    <WatchRulesList />
    </div>
  );
};

/** The lawyer's "from now on…" rules (5.3): listed in their own words; pause, resume or remove with one click. */
export const WatchRulesList: React.FC = () => {
  const [rules, setRules] = useState<DocketWatchRule[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setRules(await watchRulesApi.list());
    } catch {
      setRules([]);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);
  const change = async (rule: DocketWatchRule, status: 'active' | 'paused' | 'deleted') => {
    try {
      setBusy(rule.rule_id);
      await watchRulesApi.setStatus(rule.rule_id, status);
      await load();
    } finally {
      setBusy(null);
    }
  };
  if (!rules || rules.length === 0) return null;
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Your watch rules</div>
      <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">Standing instructions you gave Lex ("from now on, whenever …"). The docket and court mail are checked against them as they arrive; nothing here is a court deadline.</p>
      <ul className="mt-2 divide-y divide-gray-100 dark:divide-gray-700">
        {rules.map((rule) => (
          <li key={rule.rule_id} className="py-2 flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-sm text-gray-900 dark:text-gray-100">{rule.name}{rule.status === 'paused' && <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">Paused</span>}</div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{rule.description}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Fired {rule.fired_count} time{rule.fired_count === 1 ? '' : 's'}{rule.last_fired_at ? `, last ${String(rule.last_fired_at).slice(0, 10)}` : ''}.</p>
            </div>
            <div className="flex gap-2">
              <button type="button" disabled={busy === rule.rule_id} onClick={() => void change(rule, rule.status === 'paused' ? 'active' : 'paused')}
                className="px-2.5 py-1 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                {rule.status === 'paused' ? 'Resume' : 'Pause'}
              </button>
              <button type="button" disabled={busy === rule.rule_id} onClick={() => void change(rule, 'deleted')}
                className="px-2.5 py-1 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                Remove
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default DocketParalegalCard;
